import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ModernHeader from '../components/ModernHeader';

import SiteCard from '../components/SiteCard';
import SiteDisplayCard from '../components/SiteDisplayCard';
import { API_BASE_URL } from '../config/api';
import { useSettings } from '../context/SettingsContext';

import LocationSelector from '../components/LocationSelector';
import TicketDownloadModal from '../components/TicketDownloadModal';
import AdvancedFilters from '../components/AdvancedFilters';

import Footer from '../components/Footer';
import CompactBooking from './CompactBooking';

const PublicBooking = () => {
  const { settings } = useSettings();
  const { siteId } = useParams();
  const [searchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [filters, setFilters] = useState({
    category: 'all',
    search: '',
    minPrice: '',
    maxPrice: '',
    city: '',
    sortBy: 'name'
  });
  const [availableCities, setAvailableCities] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedCity, setSelectedCity] = useState('');
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [detectedCity, setDetectedCity] = useState('');
  const [showDetectedNotification, setShowDetectedNotification] = useState(false);
  const [showDisabledSiteModal, setShowDisabledSiteModal] = useState(false);
  const [disabledSiteName, setDisabledSiteName] = useState('');

  useEffect(() => {
    fetchTenantInfo();
    
    // Handle site-specific URL
    if (siteId) {
      const siteName = searchParams.get('site');
      const siteCity = searchParams.get('city');
      if (siteCity) {
        setSelectedCity(siteCity);
        localStorage.setItem('selectedCity', siteCity);
      }
      // Set selected site after sites are loaded
      if (siteName) {
        // Will be handled in useEffect when sites are loaded
      }
    } else {
      const savedCity = localStorage.getItem('selectedCity');
      if (savedCity) {
        setSelectedCity(savedCity);
      } else {
        detectUserLocation();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, searchParams]);

  useEffect(() => {
    if (selectedCity) {
      fetchSites();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCity]);

  useEffect(() => {
    // Handle site selection from URL after sites are loaded
    if (siteId && sites.length > 0) {
      const targetSite = sites.find(site => site._id === siteId);
      if (targetSite) {
        setSelectedSite(targetSite._id);
      }
    }
  }, [siteId, sites]);

  useEffect(() => {
    if (selectedSite) {
      fetchTickets();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, selectedSite]);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.search-container')) {
        setShowSearchSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchTenantInfo = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/public/tenant-info`, {
        headers: { 'x-tenant-id': 'sevenwonders' }
      });
      setTenant(response.data.tenant);
    } catch (error) {
      console.error('Error fetching tenant info:', error);
    }
  };

  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const findNearestSites = async (userLat, userLng) => {
    // Individual site coordinates for exact location detection
    const siteCoordinates = {
      // Puri District
      'jagannath temple': { lat: 19.8048, lng: 85.8315 },
      'konark sun temple': { lat: 19.8876, lng: 86.0945 },
      'chilika lake': { lat: 19.7167, lng: 85.3167 },
      'puri beach': { lat: 19.8026, lng: 85.8313 },
      
      // Ganjam District  
      'gopalpur beach': { lat: 19.2667, lng: 84.9000 },
      'taptapani hot spring': { lat: 19.0500, lng: 84.1000 },
      'rushikulya beach': { lat: 19.3167, lng: 84.8833 },
      
      // Bhubaneswar
      'lingaraj temple': { lat: 20.2387, lng: 85.8352 },
      'udayagiri caves': { lat: 20.3500, lng: 85.7667 },
      'dhauli peace pagoda': { lat: 20.1833, lng: 85.8500 },
      
      // Cuttack
      'barabati fort': { lat: 20.4625, lng: 85.8828 },
      'netaji birth place': { lat: 20.4625, lng: 85.8828 },
      
      // Angul
      'satkosia gorge': { lat: 20.5333, lng: 84.9667 },
      
      // Sambalpur
      'hirakud dam': { lat: 21.5333, lng: 83.8667 },
      'samaleswari temple': { lat: 21.4669, lng: 83.9812 },
      
      // Mayurbhanj
      'simlipal national park': { lat: 21.6667, lng: 86.5000 },
      
      // Koraput
      'jagannath sagar': { lat: 18.8120, lng: 82.7108 },
      
      // Default city coordinates as fallback
      'angul': { lat: 20.8406, lng: 85.1016 },
      'balangir': { lat: 20.7099, lng: 83.4851 },
      'balasore': { lat: 21.4942, lng: 86.9336 },
      'bargarh': { lat: 21.3344, lng: 83.6186 },
      'berhampur': { lat: 19.3149, lng: 84.7941 },
      'bhadrak': { lat: 21.0581, lng: 86.5144 },
      'bhubaneswar': { lat: 20.2961, lng: 85.8245 },
      'boudh': { lat: 20.4350, lng: 84.3300 },
      'brahmapur': { lat: 19.3149, lng: 84.7941 },
      'cuttack': { lat: 20.4625, lng: 85.8828 },
      'deogarh': { lat: 21.5424, lng: 84.7336 },
      'dhenkanal': { lat: 20.6593, lng: 85.5985 },
      'gajapati': { lat: 18.7833, lng: 84.1667 },
      'ganjam': { lat: 19.3856, lng: 85.0516 },
      'jagatsinghapur': { lat: 20.2518, lng: 86.1739 },
      'jajpur': { lat: 20.8397, lng: 86.3269 },
      'jharsuguda': { lat: 21.8644, lng: 84.0081 },
      'kalahandi': { lat: 19.9167, lng: 83.1667 },
      'kandhamal': { lat: 20.1333, lng: 84.0500 },
      'kendrapara': { lat: 20.5000, lng: 86.4167 },
      'keonjhar': { lat: 21.6297, lng: 85.5811 },
      'khordha': { lat: 20.1821, lng: 85.6186 },
      'koraput': { lat: 18.8120, lng: 82.7108 },
      'malkangiri': { lat: 18.3478, lng: 81.8811 },
      'mayurbhanj': { lat: 21.9297, lng: 86.7350 },
      'nabarangpur': { lat: 19.2306, lng: 82.5497 },
      'nayagarh': { lat: 20.1297, lng: 85.0969 },
      'nuapada': { lat: 20.8047, lng: 82.5408 },
      'puri': { lat: 19.8135, lng: 85.8312 },
      'rayagada': { lat: 19.1672, lng: 83.4158 },
      'rourkela': { lat: 22.2604, lng: 84.8536 },
      'sambalpur': { lat: 21.4669, lng: 83.9812 },
      'subarnapur': { lat: 20.9167, lng: 83.9000 },
      'sundargarh': { lat: 22.1167, lng: 84.0167 }
    };

    const ticketsResponse = await axios.get(`${API_BASE_URL}/api/public/test-tickets`);
    const allTickets = ticketsResponse.data.tickets || [];
    
    let nearestSites = [];
    
    // Calculate distance to each site
    allTickets.forEach(ticket => {
      const siteName = ticket.name.toLowerCase();
      const cityName = ticket.city.toLowerCase();
      
      // Try to find exact site coordinates first, then fallback to city
      const coords = siteCoordinates[siteName] || siteCoordinates[cityName];
      
      if (coords) {
        const distance = calculateDistance(userLat, userLng, coords.lat, coords.lng);
        nearestSites.push({
          ticket,
          distance,
          hasExactLocation: !!siteCoordinates[siteName]
        });
        console.log(`Distance to ${ticket.name}: ${distance.toFixed(2)} km ${siteCoordinates[siteName] ? '(exact)' : '(city)'}`);
      }
    });
    
    // Sort by distance and return nearest city
    nearestSites.sort((a, b) => a.distance - b.distance);
    
    if (nearestSites.length > 0) {
      const nearest = nearestSites[0];
      console.log(`Nearest site: ${nearest.ticket.name} in ${nearest.ticket.city} (${nearest.distance.toFixed(2)} km away)`);
      return nearest.ticket.city;
    }
    
    return null;
  };

  const fallbackToAvailableCities = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/public/test-tickets`);
      const tickets = response.data.tickets || [];
      
      if (tickets.length > 0) {
        const availableCities = [...new Set(tickets.map(t => t.city))].sort();
        if (availableCities.length > 0) {
          const firstCity = availableCities[0];
          setSelectedCity(firstCity);
          localStorage.setItem('selectedCity', firstCity);
          fetchSites();
          setLocationLoading(false);
          setLocationError('');
          setDetectedCity(`${firstCity} (nearest available)`);
          setShowDetectedNotification(true);
          setTimeout(() => setShowDetectedNotification(false), 4000);
          console.log('Fallback to first available city:', firstCity);
          return;
        }
      }
      
      setLocationError('No sites available');
      setLocationLoading(false);
      setShowLocationModal(true);
    } catch (error) {
      setLocationError('Failed to load locations');
      setLocationLoading(false);
      setShowLocationModal(true);
    }
  };

  const detectUserLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported');
      setShowLocationModal(true);
      return;
    }

    setLocationLoading(true);
    setLocationError('');

    const tryLocation = (highAccuracy = true) => {
      const options = {
        enableHighAccuracy: highAccuracy,
        timeout: highAccuracy ? 10000 : 15000,
        maximumAge: highAccuracy ? 60000 : 300000
      };

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          console.log(`Location: ${latitude}, ${longitude} (accuracy: ${accuracy}m)`);
          
          try {
            // Use reverse geocoding to get city name
            const response = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`);
            const data = await response.json();
            
            const detectedCityName = data.city || data.locality || data.principalSubdivision;
            console.log('Detected city from geocoding:', detectedCityName);
            
            if (detectedCityName) {
              // Check if we have tickets for this city
              const ticketsResponse = await axios.get(`${API_BASE_URL}/api/public/test-tickets`);
              const allTickets = ticketsResponse.data.tickets || [];
              const availableCities = [...new Set(allTickets.map(t => t.city.toLowerCase()))];
              
              const matchingCity = availableCities.find(city => 
                city.includes(detectedCityName.toLowerCase()) || 
                detectedCityName.toLowerCase().includes(city)
              );
              
              if (matchingCity) {
                const originalCityName = allTickets.find(t => t.city.toLowerCase() === matchingCity)?.city;
                setSelectedCity(originalCityName);
                localStorage.setItem('selectedCity', originalCityName);
                fetchTickets(originalCityName);
                setLocationLoading(false);
                setLocationError('');
                setDetectedCity(originalCityName);
                setShowDetectedNotification(true);
                setTimeout(() => setShowDetectedNotification(false), 4000);
                console.log('Found matching city:', originalCityName);
                return;
              }
            }
            
            // Try to find nearest city by distance
            console.log('No exact match found, searching for nearest city...');
            const nearestCity = await findNearestSites(latitude, longitude);
            
            if (nearestCity) {
              setSelectedCity(nearestCity);
              localStorage.setItem('selectedCity', nearestCity);
              fetchTickets(nearestCity);
              setLocationLoading(false);
              setLocationError('');
              setDetectedCity(`${nearestCity} (nearest)`);
              setShowDetectedNotification(true);
              setTimeout(() => setShowDetectedNotification(false), 4000);
              console.log('Using nearest city:', nearestCity);
              return;
            }
            
            // If no nearest city found, use fallback
            console.log('No nearby cities found, using fallback');
            await fallbackToAvailableCities();
          } catch (error) {
            console.error('Geocoding error:', error);
            await fallbackToAvailableCities();
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
          if (highAccuracy && error.code === error.TIMEOUT) {
            console.log('Retrying with low accuracy');
            tryLocation(false);
            return;
          }
          
          let errorMessage = 'Location access denied';
          switch(error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location unavailable';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location timeout';
              break;
            default:
              errorMessage = 'Location error';
              break;
          }
          setLocationError(errorMessage);
          setLocationLoading(false);
          setShowLocationModal(true);
        },
        options
      );
    };

    tryLocation(true);
  };

  const fetchSites = async () => {
    try {
      const ticketsResponse = await axios.get(`${API_BASE_URL}/api/public/test-tickets`);
      const tickets = ticketsResponse.data.tickets || [];
      console.log('All tickets:', tickets.length);
      
      const sitesResponse = await axios.get(`${API_BASE_URL}/api/public/sites`, {
        headers: { 'x-tenant-id': 'sevenwonders' }
      }).catch(() => ({ data: { sites: [] } }));
      const sitesWithImages = sitesResponse.data.sites || [];
      console.log('All sites from DB:', sitesWithImages.length);
      
      const cityTickets = selectedCity ? 
        tickets.filter(ticket => ticket.city.toLowerCase() === selectedCity.toLowerCase()) : 
        tickets;
      console.log(`City tickets for ${selectedCity}:`, cityTickets.length);
      
      const siteMap = new Map();
      
      // Group tickets by siteId
      const ticketGroups = new Map();
      cityTickets.forEach(ticket => {
        if (ticket.siteId) {
          if (!ticketGroups.has(ticket.siteId)) {
            ticketGroups.set(ticket.siteId, []);
          }
          ticketGroups.get(ticket.siteId).push(ticket);
        }
      });
      
      // STEP 1: Add ALL database sites for the city (database names have absolute priority)
      const filteredSites = sitesWithImages
        .filter(site => site.city.toLowerCase() === selectedCity.toLowerCase());
      
      filteredSites.forEach(site => {
        const siteTickets = ticketGroups.get(site._id) || [];
        siteMap.set(site._id, {
          _id: site._id,
          name: site.name, // Database site name - NEVER override this
          city: site.city,
          description: site.description || `Visit ${site.name} in ${site.city}`,
          images: site.images || [],
          category: site.category || 'other',
          tickets: siteTickets,
          isActive: site.isActive
        });
        // Remove processed tickets
        ticketGroups.delete(site._id);
      });
      
      // STEP 2: Only process remaining tickets that don't have database sites
      ticketGroups.forEach((tickets, siteId) => {
        const firstTicket = tickets[0];
        let siteName = firstTicket.name.replace(/\s+(entry|parking|chappal stand|puja|event|photoshoot|pass|ticket).*$/i, '').trim();
        
        siteMap.set(siteId, {
          _id: siteId,
          name: siteName,
          city: firstTicket.city,
          description: `Visit ${siteName} in ${firstTicket.city}`,
          images: [],
          category: 'other',
          tickets: tickets,
          isActive: true
        });
      });
      
      // STEP 3: Handle orphan tickets without siteId
      const orphanTickets = cityTickets.filter(ticket => !ticket.siteId);
      const orphanGroups = new Map();
      
      orphanTickets.forEach(ticket => {
        const baseName = ticket.name.replace(/\s+(entry|parking|chappal stand|puja|event|photoshoot|pass|ticket).*$/i, '').trim();
        const key = `${baseName}-${ticket.city}`;
        
        if (!orphanGroups.has(key)) {
          orphanGroups.set(key, []);
        }
        orphanGroups.get(key).push(ticket);
      });
      
      orphanGroups.forEach((tickets, key) => {
        const firstTicket = tickets[0];
        const siteName = firstTicket.name.replace(/\s+(entry|parking|chappal stand|puja|event|photoshoot|pass|ticket).*$/i, '').trim();
        
        siteMap.set(key, {
          _id: key.toLowerCase().replace(/\s+/g, '-'),
          name: siteName,
          city: firstTicket.city,
          description: `Visit ${siteName} in ${firstTicket.city}`,
          images: [],
          category: 'other',
          tickets: tickets
        });
      });
      
      const finalSites = Array.from(siteMap.values());
      setSites(finalSites);
    } catch (error) {
      console.error('Error fetching sites:', error);
      setSites([]);
    }
  };

  const fetchTickets = async () => {
    if (!selectedSite) return;
    
    try {
      // Use the tickets already stored in the selected site
      let filteredTickets = selectedSite.tickets || [];
      
      // Apply advanced filters
      filteredTickets = applyFilters(filteredTickets);
      
      setTickets(filteredTickets);
      console.log('Fetched tickets for site:', filteredTickets.length);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setTickets([]);
    }
  };



  const applyFilters = (ticketList) => {
    let filtered = [...ticketList];
    
    // Category filter
    if (filters.category !== 'all') {
      filtered = filtered.filter(ticket => ticket.category === filters.category);
    }
    
    // Search filter
    if (filters.search) {
      filtered = filtered.filter(ticket => 
        ticket.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        ticket.description?.toLowerCase().includes(filters.search.toLowerCase())
      );
    }
    
    // Price range filter
    if (filters.minPrice) {
      filtered = filtered.filter(ticket => ticket.price >= parseFloat(filters.minPrice));
    }
    if (filters.maxPrice) {
      filtered = filtered.filter(ticket => ticket.price <= parseFloat(filters.maxPrice));
    }
    
    // City filter (from advanced filters)
    if (filters.city) {
      filtered = filtered.filter(ticket => ticket.city === filters.city);
    }
    
    // Sorting
    switch (filters.sortBy) {
      case 'price_low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price_high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'newest':
        filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        break;
      default: // name
        filtered.sort((a, b) => a.name.localeCompare(b.name));
    }
    
    return filtered;
  };

  const handleBookNow = (ticket) => {
    setSelectedTicket(ticket);
  };

  const handleSiteClick = (site) => {
    if (site.isActive === false) {
      setDisabledSiteName(site.name);
      setShowDisabledSiteModal(true);
    } else {
      setSelectedSite(site);
    }
  };

  const handleLocationSelect = (city) => {
    console.log('Selected city:', city);
    setSelectedCity(city);
    localStorage.setItem('selectedCity', city);
    setShowLocationModal(false);
    
    // Close booking page if open
    setSelectedTicket(null);
    
    // Clear search and reset filters when city changes
    setFilters({
      category: 'all',
      search: '',
      minPrice: '',
      maxPrice: '',
      city: '',
      sortBy: 'name'
    });
    setSearchSuggestions([]);
    setShowSearchSuggestions(false);
    
    // Reset selected site when city changes
    setSelectedSite(null);
  };
  
  const handleSearchChange = (value) => {
    setFilters({...filters, search: value});
    
    if (value.length > 0 && selectedCity && tickets.length > 0) {
      const cityTickets = tickets.filter(ticket => 
        ticket.name.toLowerCase().includes(value.toLowerCase())
      );
      setSearchSuggestions(cityTickets.slice(0, 5));
      setShowSearchSuggestions(true);
    } else {
      setShowSearchSuggestions(false);
      setSearchSuggestions([]);
    }
  };
  
  const handleSuggestionClick = (ticket) => {
    setFilters({...filters, search: ticket.name});
    setShowSearchSuggestions(false);
    handleBookNow(ticket);
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)'
    }}>
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .fade-in-up {
            animation: fadeInUp 0.6s ease-out;
          }
          .site-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 15px 40px rgba(0,0,0,0.15);
          }
          .site-display-card:hover {
            transform: translateY(-8px) scale(1.02);
            box-shadow: 0 20px 60px rgba(0,0,0,0.2);
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes slideInRight {
            from {
              opacity: 0;
              transform: translateX(100px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(50px) scale(0.95);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
        `}
      </style>
      
      {/* Detected City Notification */}
      {showDetectedNotification && (
        <div style={{
          position: 'fixed',
          top: '100px',
          right: '20px',
          background: 'linear-gradient(45deg, #06b6d4, #10b981)',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '50px',
          boxShadow: '0 8px 25px rgba(16, 185, 129, 0.3)',
          zIndex: 1001,
          fontSize: '14px',
          fontWeight: '600',
          animation: 'slideInRight 0.5s ease-out',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          📍 Location detected: {detectedCity.charAt(0).toUpperCase() + detectedCity.slice(1)}
        </div>
      )}
      
      <ModernHeader 
        tenant={tenant} 
        selectedCity={selectedCity} 
        onCitySelect={handleLocationSelect}
        onShowTicketModal={() => setShowTicketModal(true)}
        locationLoading={locationLoading}
        locationError={locationError}
        onDetectLocation={detectUserLocation}
      />
      
      {/* Compact Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 50%, #10b981 100%)',
        minHeight: window.innerWidth <= 768 ? '35vh' : '40vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          animation: 'float 6s ease-in-out infinite'
        }} />
        
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: window.innerWidth <= 768 ? '0 12px' : '0 16px', width: '100%', zIndex: 2 }}>
          <div style={{ textAlign: 'center', color: 'white' }}>
            <h1 style={{
              fontSize: window.innerWidth <= 480 ? '1.3rem' : 'clamp(1.5rem, 5vw, 2.5rem)',
              fontWeight: '700',
              marginBottom: window.innerWidth <= 768 ? '8px' : '16px',
              textShadow: '0 4px 20px rgba(0,0,0,0.3)',
              lineHeight: '1.1'
            }}>
              Discover {selectedCity ? selectedCity.charAt(0).toUpperCase() + selectedCity.slice(1) : 'Amazing Sites'}
            </h1>
            <p style={{
              fontSize: window.innerWidth <= 768 ? '0.9rem' : '1.1rem',
              marginBottom: window.innerWidth <= 768 ? '16px' : '24px',
              opacity: 0.9,
              maxWidth: '500px',
              margin: `0 auto ${window.innerWidth <= 768 ? '16px' : '24px'}`,
              padding: '0 16px'
            }}>
              Book tickets instantly for temples, beaches and attractions
            </p>
            
            {/* Compact Search Bar */}
            <div className="search-container" style={{
              maxWidth: window.innerWidth <= 768 ? '100%' : '500px',
              margin: '0 auto',
              position: 'relative',
              padding: window.innerWidth <= 768 ? '0 8px' : '0 16px'
            }}>
              <input
                type="text"
                placeholder={selectedCity ? `Search in ${selectedCity.charAt(0).toUpperCase() + selectedCity.slice(1)}...` : "Search for parks, temples, beaches..."}
                value={filters.search}
                onChange={(e) => handleSearchChange(e.target.value)}
                onFocus={() => filters.search && setShowSearchSuggestions(true)}
                style={{
                  width: '100%',
                  padding: window.innerWidth <= 768 ? '14px 50px 14px 20px' : '16px 55px 16px 24px',
                  fontSize: window.innerWidth <= 768 ? '15px' : '16px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderRadius: '50px',
                  background: 'rgba(255,255,255,0.98)',
                  backdropFilter: 'blur(15px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.6)',
                  outline: 'none',
                  color: '#2d3748',
                  fontWeight: '500',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 20px 60px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.6)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 15px 50px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.6)';
                }}
              />
              <div style={{
                position: 'absolute',
                right: window.innerWidth <= 768 ? '4px' : '6px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 50%, #10b981 100%)',
                width: window.innerWidth <= 768 ? '38px' : '42px',
                height: window.innerWidth <= 768 ? '38px' : '42px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: window.innerWidth <= 768 ? '16px' : '18px',
                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                transition: 'all 0.3s ease',
                border: '2px solid rgba(255,255,255,0.3)'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-50%) scale(1.05)';
                e.target.style.boxShadow = '0 12px 35px rgba(102, 126, 234, 0.5)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(-50%) scale(1)';
                e.target.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.4)';
              }}>
                🔍
              </div>
              
              {/* Search Suggestions */}
              {showSearchSuggestions && searchSuggestions.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: 'white',
                  borderRadius: '20px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                  zIndex: 1000,
                  marginTop: '8px',
                  overflow: 'hidden'
                }}>
                  {searchSuggestions.map(ticket => (
                    <div
                      key={ticket._id}
                      onClick={() => handleSuggestionClick(ticket)}
                      style={{
                        padding: '16px 24px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        transition: 'background 0.2s'
                      }}
                      onMouseOver={(e) => e.target.style.background = '#f8fafc'}
                      onMouseOut={(e) => e.target.style.background = 'white'}
                    >
                      <span style={{ fontSize: '20px' }}>
                        {ticket.name.includes('Beach') ? '🏖️' : 
                         ticket.name.includes('Temple') ? '🛕' : 
                         ticket.name.includes('Lake') ? '🏞️' : '🌳'}
                      </span>
                      <div>
                        <div style={{ fontWeight: '600', color: '#2d3748' }}>{ticket.name}</div>
                        <div style={{ fontSize: '14px', color: '#718096' }}>₹{ticket.price} • {ticket.category.replace('_', ' ')}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: window.innerWidth <= 768 ? '24px 16px' : '32px 24px' }}>
        {!selectedTicket && selectedCity && (
          <>
            {/* Advanced Filters */}
            <AdvancedFilters
              filters={filters}
              onFiltersChange={setFilters}
              categories={[
                { key: 'all', label: 'All', icon: '🎯' },
                { key: 'entry_ticket', label: 'Entry', icon: '🌳' },
                { key: 'parking_ticket', label: 'Parking', icon: '🅿️' },
                { key: 'chappal_stand_ticket', label: 'Chappal Stand', icon: '👟' },
                { key: 'puja_ticket', label: 'Puja', icon: '🙏' },
                { key: 'photo_shoot_ticket', label: 'Photo Shoots', icon: '📸' },
                { key: 'function_hall_ticket', label: 'Function Hall', icon: '🎉' },
                { key: 'event_ticket', label: 'Events', icon: '🎭' },
                { key: 'pass_ticket', label: 'Pass Tickets', icon: '📅' },
                { key: 'other', label: 'Other', icon: '🏛️' }
              ]}
              cities={availableCities}
            />

            {/* Compact Stats Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(3, 1fr)',
              gap: window.innerWidth <= 768 ? '12px' : '16px',
              marginBottom: window.innerWidth <= 768 ? '24px' : '32px'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #06b6d4, #10b981)',
                color: 'white',
                padding: window.innerWidth <= 768 ? '16px' : '20px',
                borderRadius: '16px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: window.innerWidth <= 768 ? '1.5rem' : '1.8rem', marginBottom: '6px' }}>🎫</div>
                <div style={{ fontSize: window.innerWidth <= 768 ? '1.2rem' : '1.4rem', fontWeight: '700' }}>{selectedSite ? tickets.length : sites.length}</div>
                <div style={{ opacity: 0.9, fontSize: window.innerWidth <= 768 ? '0.8rem' : '0.9rem' }}>{selectedSite ? 'Tickets' : 'Sites'}</div>
              </div>
              <div style={{
                background: 'linear-gradient(135deg, #ff6b6b, #feca57)',
                color: 'white',
                padding: window.innerWidth <= 768 ? '16px' : '20px',
                borderRadius: '16px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: window.innerWidth <= 768 ? '1.5rem' : '1.8rem', marginBottom: '6px' }}>📍</div>
                <div style={{ fontSize: window.innerWidth <= 768 ? '1.2rem' : '1.4rem', fontWeight: '700' }}>{selectedCity.charAt(0).toUpperCase() + selectedCity.slice(1)}</div>
                <div style={{ opacity: 0.9, fontSize: window.innerWidth <= 768 ? '0.8rem' : '0.9rem' }}>Location</div>
              </div>
              <div style={{
                background: 'linear-gradient(135deg, #4ecdc4, #44a08d)',
                color: 'white',
                padding: window.innerWidth <= 768 ? '16px' : '20px',
                borderRadius: '16px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: window.innerWidth <= 768 ? '1.5rem' : '1.8rem', marginBottom: '6px' }}>⚡</div>
                <div style={{ fontSize: window.innerWidth <= 768 ? '1.2rem' : '1.4rem', fontWeight: '700' }}>Instant</div>
                <div style={{ opacity: 0.9, fontSize: window.innerWidth <= 768 ? '0.8rem' : '0.9rem' }}>Booking</div>
              </div>
            </div>

            {/* Compact Sites/Tickets Grid */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', 
              gap: window.innerWidth <= 768 ? '16px' : '20px',
              marginBottom: window.innerWidth <= 768 ? '24px' : '32px'
            }}>
              {!selectedSite ? (
                // Show sites
                sites.map((site, index) => (
                  <div 
                    key={`site-${site._id}-${index}`}
                    className="fade-in-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <SiteDisplayCard 
                      site={site}
                      onSiteClick={handleSiteClick}
                      onDirectBooking={site.isActive === false ? null : setSelectedTicket}
                    />
                  </div>
                ))
              ) : (
                // Show tickets for selected site
                <>
                  <div style={{ gridColumn: '1 / -1', marginBottom: '20px' }}>
                    <button 
                      onClick={() => setSelectedSite(null)}
                      style={{
                        background: 'linear-gradient(45deg, #667eea, #764ba2)',
                        color: 'white',
                        border: 'none',
                        padding: '12px 24px',
                        borderRadius: '25px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      ← Back to Sites
                    </button>
                    <h2 style={{ margin: '16px 0 0 0', color: '#2d3748' }}>Tickets for {selectedSite.name}</h2>
                  </div>
                  {tickets.map((ticket, index) => (
                    <div 
                      key={`ticket-${ticket._id}-${index}`}
                      className="fade-in-up"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <SiteCard 
                        site={ticket} 
                        onBook={handleBookNow}
                      />
                    </div>
                  ))}
                </>
              )}
            </div>
          </>
        )}
        
        {/* Modern Booking Modal */}
        {selectedTicket && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
            animation: 'fadeIn 0.3s ease-out'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              borderRadius: '24px',
              maxWidth: '650px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'hidden',
              position: 'relative',
              boxShadow: '0 25px 50px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.3)',
              animation: 'slideUp 0.4s ease-out'
            }}>
              <button
                onClick={() => setSelectedTicket(null)}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: 'rgba(0,0,0,0.1)',
                  backdropFilter: 'blur(10px)',
                  border: 'none',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: '#64748b',
                  zIndex: 1001,
                  transition: 'all 0.2s ease',
                  fontWeight: '600'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = 'rgba(239, 68, 68, 0.1)';
                  e.target.style.color = '#ef4444';
                  e.target.style.transform = 'scale(1.1)';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'rgba(0,0,0,0.1)';
                  e.target.style.color = '#64748b';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                ×
              </button>
              <div style={{
                maxHeight: '90vh',
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                scrollbarColor: '#cbd5e1 transparent'
              }}>
                <CompactBooking 
                  selectedTicket={selectedTicket}
                  onBack={() => setSelectedTicket(null)}
                />
              </div>
            </div>
          </div>
        )}
        {!selectedTicket && !selectedSite && sites.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: window.innerWidth <= 768 ? '32px 16px' : '40px 20px',
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            borderRadius: '20px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <div style={{ fontSize: window.innerWidth <= 768 ? '3rem' : '4rem', marginBottom: window.innerWidth <= 768 ? '16px' : '20px' }}>
              {filters.category === 'event_ticket' ? '🎭' : 
               filters.category === 'entry_ticket' ? '🌳' : 
               filters.category === 'other' ? '🏛️' : '🎫'}
            </div>
            <h3 style={{ 
              fontSize: window.innerWidth <= 768 ? '1.4rem' : '1.6rem', 
              fontWeight: '700', 
              color: '#2d3748',
              marginBottom: '12px'
            }}>
              No Sites Found
            </h3>
            <p style={{ 
              color: '#64748b', 
              fontSize: window.innerWidth <= 768 ? '1rem' : '1.1rem',
              marginBottom: window.innerWidth <= 768 ? '20px' : '24px',
              maxWidth: '400px',
              margin: `0 auto ${window.innerWidth <= 768 ? '20px' : '24px'}`,
              lineHeight: '1.5'
            }}>
              We couldn't find any sites in {selectedCity?.charAt(0).toUpperCase() + selectedCity?.slice(1)}. Try adjusting your filters!
            </p>
            
            <div style={{ 
              display: 'flex', 
              gap: window.innerWidth <= 768 ? '12px' : '16px', 
              justifyContent: 'center',
              flexWrap: 'wrap',
              marginBottom: window.innerWidth <= 768 ? '20px' : '24px'
            }}>
              <button
                onClick={() => setFilters({ category: 'all', search: '', minPrice: '', maxPrice: '', city: '', sortBy: 'name' })}
                style={{
                  background: 'linear-gradient(45deg, #667eea, #764ba2)',
                  color: 'white',
                  border: 'none',
                  padding: window.innerWidth <= 768 ? '12px 20px' : '14px 24px',
                  borderRadius: '25px',
                  fontSize: window.innerWidth <= 768 ? '14px' : '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                🔄 Clear Filters
              </button>
              
              <button
                onClick={() => setShowLocationModal(true)}
                style={{
                  background: 'linear-gradient(45deg, #48bb78, #38a169)',
                  color: 'white',
                  border: 'none',
                  padding: window.innerWidth <= 768 ? '12px 20px' : '14px 24px',
                  borderRadius: '25px',
                  fontSize: window.innerWidth <= 768 ? '14px' : '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 6px 20px rgba(72, 187, 120, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                📍 Change Location
              </button>
            </div>
            
            <div style={{
              background: 'rgba(255,255,255,0.7)',
              padding: window.innerWidth <= 768 ? '16px' : '20px',
              borderRadius: '12px',
              maxWidth: '350px',
              margin: '0 auto'
            }}>
              <h4 style={{ 
                fontSize: window.innerWidth <= 768 ? '1rem' : '1.1rem', 
                fontWeight: '600', 
                color: '#4a5568',
                marginBottom: '10px'
              }}>💡 Quick Tips:</h4>
              <div style={{ 
                textAlign: 'left', 
                color: '#64748b',
                fontSize: window.innerWidth <= 768 ? '0.85rem' : '0.9rem',
                lineHeight: '1.6'
              }}>
                • Select "All" categories<br/>
                • Clear search terms<br/>
                • Try nearby cities<br/>
                • New sites added daily!
              </div>
            </div>
          </div>
        )}
        
        {showLocationModal && (
          <LocationSelector onLocationSelect={handleLocationSelect} />
        )}
        
        <TicketDownloadModal 
          isOpen={showTicketModal}
          onClose={() => setShowTicketModal(false)}
        />
        
        {/* Disabled Site Modal */}
        {showDisabledSiteModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: window.innerWidth <= 768 ? '24px' : '32px',
              maxWidth: '400px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 15px 40px rgba(0,0,0,0.25)'
            }}>
              <div style={{ fontSize: window.innerWidth <= 768 ? '3rem' : '3.5rem', marginBottom: window.innerWidth <= 768 ? '16px' : '20px' }}>🚧</div>
              <h3 style={{
                fontSize: window.innerWidth <= 768 ? '1.2rem' : '1.4rem',
                fontWeight: '700',
                color: '#2d3748',
                marginBottom: '12px'
              }}>
                Site Temporarily Unavailable
              </h3>
              <p style={{
                color: '#64748b',
                fontSize: window.innerWidth <= 768 ? '1rem' : '1.1rem',
                marginBottom: window.innerWidth <= 768 ? '20px' : '24px',
                lineHeight: '1.5'
              }}>
                Thanks for showing interest in <strong>{disabledSiteName}</strong>! We are trying to enable this site as soon as possible.
              </p>
              <button
                onClick={() => setShowDisabledSiteModal(false)}
                style={{
                  background: 'linear-gradient(45deg, #06b6d4, #10b981)',
                  color: 'white',
                  border: 'none',
                  padding: window.innerWidth <= 768 ? '10px 24px' : '12px 28px',
                  borderRadius: '20px',
                  fontSize: window.innerWidth <= 768 ? '14px' : '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 6px 20px rgba(59, 130, 246, 0.3)'
                }}
              >
                Got it!
              </button>
            </div>
          </div>
        )}
      </div>
      
      <Footer />
      

    </div>
  );
};

export default PublicBooking;